/**
 * Advantage.js — "Leo Cussen Advantage" benefits section (home page).
 *
 * Renders a row of benefit blocks (pictogram + title) over a background image.
 * Content (heading, each block's title, count and order) is CMS-driven: it
 * comes from the admin panel's `home_page_details → advantages.tabs`, fetched by
 * HomePage and passed in as props.
 *
 * Pictograms: each block's icon is a Lottie animation BUNDLED in src/lottie,
 * matched to the block by a keyword in its title so the pairing survives the
 * portal reordering/adding blocks. If a title matches no Lottie, the block falls
 * back to the CMS `icon_url` static image — so it can never render blank.
 *
 * Motion: each Lottie plays ONCE when its block first scrolls into view, in sync
 * with the shared fade-up reveal (App.js observer + .advantage_block CSS). Users
 * with `prefers-reduced-motion` see the static CMS image instead.
 *
 * Props:
 *  - AdvantageTitle {string}  Section heading.
 *  - AdvantageList {Array}    List of { icon_url, title } objects.
 *  - GAurl {string}           Google Ads click-ID query string (unused here).
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Lottie from 'lottie-react';

// ─── Internal — images ────────────────────────────────────────────────────────
import Bg from "../../images/bg.jpg";


/**
 * Title-keyword → bundled Lottie. First rule whose keyword appears in the
 * (tag-stripped, lower-cased) block title wins. "points" is deliberately avoided
 * because it appears in multiple titles. Add a rule here if the portal
 * introduces a new advantage that should animate.
 *
 *   "Quality Presenters…"  → 03 Personalised Support
 *   "Track all your CPD…"  → 04 Guaranteed Placement
 *   "Efficient way…"       → 06 Job Ready Confidence
 */


/**
 * IconErrorBoundary — isolates each icon so a Lottie/render failure falls back
 * to a static image instead of tearing down the whole home page (React unmounts
 * the entire tree on an uncaught render error).
 */
class IconErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/** Strips HTML tags (titles contain <br/>) and lower-cases for keyword matching. */
const stripHtml = (value) => (value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();



/** True when the visitor has asked the OS to reduce motion. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * LottieIcon — renders one bundled pictogram animation that plays a single time
 * when it scrolls into the viewport. Reduced-motion users get the static image.
 */
const LottieIcon = ({ animationData, fallbackSrc, alt, variant }) => {
  const containerRef = useRef(null);
  const lottieRef = useRef(null);
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const block = el.closest('.advantage_block');
    let played = false;
    const play = () => {
      if (played) return;
      played = true;
      if (lottieRef.current) lottieRef.current.play();
    };

    // Blocks reveal with a staggered entrance animation, so a geometric
    // "in view" check plays the pictogram too early — e.g. the second row is
    // still opacity:0 (reveal delayed up to ~2s) while its animation runs and
    // finishes, so it's never seen. Instead, play in sync with the reveal: when
    // the block's entrance animation actually STARTS (after its delay).
    const onRevealStart = (e) => {
      if (e.target === block) play();
    };
    if (block) block.addEventListener('animationstart', onRevealStart);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target); // decide once
          // If the block is waiting to reveal, let its animationstart play the
          // pictogram in sync. Otherwise (no reveal system, or already
          // revealing/revealed) play now.
          const waitingToReveal =
            block &&
            block.classList.contains('lc-reveal-pending') &&
            !block.classList.contains('lc-reveal');
          if (!waitingToReveal) play();
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (block) block.removeEventListener('animationstart', onRevealStart);
    };
  }, [reduceMotion]);

  // Respect the user's motion preference: show the static CMS image, no animation.
  if (reduceMotion) {
    return <img src={fallbackSrc} alt={alt} />;
  }

  return (
    <div className={`advantage_lottie${variant ? ` advantage_lottie--${variant}` : ''}`} ref={containerRef}>
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={false}
        autoplay={false}
        aria-label={alt}
      />
    </div>
  );
};

// ─── Single advantage item — handles its own lottie fetch ─────────────────────
const AdvantageItem = ({ advantages }) => {
    const alt     = stripHtml(advantages.title ?? '');
    const iconUrl = advantages.icon_url ?? '';

    // Detect what type of data icon_url contains
    const isJsonString = iconUrl.trim().startsWith('{');  // raw Lottie JSON string
    const isLottieUrl  = iconUrl.endsWith('.txt') || iconUrl.endsWith('.json'); // URL to fetch
    const isSvgCode    = iconUrl.trim().startsWith('<svg') || iconUrl.trim().startsWith('<?xml');

    // Parse inline JSON directly — no fetch needed
    const [lottieData] = useState(() => {
        if (isJsonString) {
            try { return JSON.parse(iconUrl); } catch { return null; }
        }
        return null;
    });

    return (
        <div className='advantage_block d-flex align-items-center'>
            <div className='advantage_block_icon'>
                {(isJsonString || isLottieUrl) && lottieData ? (
                    // Lottie animation — from inline JSON or fetched URL
                    <IconErrorBoundary fallback={<img src={iconUrl} alt={alt} />}>
                        <LottieIcon animationData={lottieData} fallbackSrc={iconUrl} alt={alt} />
                    </IconErrorBoundary>
                ) : isSvgCode ? (
                    // Raw SVG markup string
                    <span
                        className="advantage_svg_icon"
                        dangerouslySetInnerHTML={{ __html: iconUrl }}
                        aria-label={alt}
                    />
                ) : (
                    // SVG/PNG/JPG URL
                    <img src={iconUrl} alt={alt} />
                )}
            </div>
            <div className='advantage_block_content'>
                <h5 dangerouslySetInnerHTML={{ __html: advantages.title }} className="mb-0"></h5>
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * AdvantageSection — "Leo Cussen Advantage" benefits row.
 * See file-level JSDoc for full props description.
 */
export const AdvantageSection = (props) => {
  return (
    <div className='advantage_wrapper'>
      <Container>
        <div className='advantage_inner' style={{ backgroundImage: "url(" + Bg + ")" }}>
          <div className='title_wrap text-center'>
            <h2 className='text-white'>{props.AdvantageTitle}</h2>
          </div>
          <div className='advantage_block_wrap'>

            <Row className="align-items-center justify-content-center justify-content-lg-between">
                {props.AdvantageList.map((advantages, index) => (
                    <Col xs={12} md="auto" key={index}>
                        <AdvantageItem advantages={advantages} />
                    </Col>
                ))}
            </Row>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default AdvantageSection;
